import {ReactiveController} from 'lit';
import type {O2dAreaElement} from '../elements/o2d-area.element';
import type {CellEventDetail} from '../types/cell-event-detail.interface';
import {filter, switchMap, Subscription, fromEvent, merge, Subject, takeUntil, pairwise, timer, map} from 'rxjs';
import type {AreaCellPosition} from '../interfaces/area-cell-position.interface';

/**
 * Event name constant for cell-hover-changed events.
 * Mirrors the CELL_HOVER_CHANGED_EVENT exported from index.ts
 * to avoid circular imports (index.ts -> o2d-area.element -> this controller).
 */
const CELL_HOVER_CHANGED_EVENT = 'cell-hover-changed' as const;

/**
 * AreaCellTrackingController handles hover events on viewport cells with RxJS-based event streams.
 *
 * Architecture:
 * - Layer 1: AreaCell Position Observable (areaCellPosition$ from O2dAreaElement)
 *   - mousemove → map to AreaCellPosition → distinctUntilChanged (cell-based dedup)
 *
 * - Layer 2: Consumer Layer (this controller)
 *   - Enter events: debounceTime(120) → switchMap(buildCellEventPayload) → dispatch 'enter'
 *   - Leave events: mouseleave / blur / visibilitychange / cell-switch (via pairwise)
 *   - Drag-suppression: pointerdown/pointerup filter via RxJS
 *
 * Features:
 * - 120ms delay before enter event (prevents accidental hovers)
 * - Cell-based deduplication (not item-based) → Multi-cell items dispatch events per cell
 * - Empty-cell support (items.length === 0 still dispatches events)
 * - Drag-suppression: No hover events during right-click drag scrolling
 * - switchMap cancels pending async operations on rapid cell-switch
 * - Window blur / visibility change: Auto-leave
 * - Leave on every cell-switch: Consistent enter/leave pairing
 */
export class AreaCellTrackingController implements ReactiveController {
    private readonly host: O2dAreaElement;

    /**
     * Suppress hover events during drag scrolling (right-click drag)
     */
    private suppressHover = false;

    /**
     * RxJS subscription for areaCellPosition$ stream.
     * Handles debounceTime(120) + switchMap to buildCellEventPayload.
     */
    private subscription?: Subscription;

    /**
     * Stored payload from enter event for leave reuse
     */
    private enteredPayload: CellEventDetail | null = null;

    /**
     * Subject for cleanup signal on hostDisconnected
     */
    private destroy$ = new Subject<void>();

    /**
     * Subject to cancel pending debounce on mouseleave/blur/drag
     * Used to cancel the debounceTime(120) timer when mouse leaves area before hover completes
     */
    private cancelPendingEnter$ = new Subject<void>();


    constructor(host: O2dAreaElement) {
        this.host = host;
        host.addController(this);
    }

    /**
     * Lifecycle: Called when host is connected to DOM
     */
    hostConnected(): void {
        // Opt-in guard: Only activate when enableCellHoverEvent is set
        if (!this.host.areaConfiguration?.enableCellHoverEvent) {
            return;
        }

        // Check if areaCellPosition$ stream exists (lazy initialization)
        if (!this.host.areaCellPosition$) {
            return;
        }

        const areaCellPosition$ = this.host.areaCellPosition$;

        // T4.8-T4.12: RxJS Enter events pipeline
        // switchMap cancels previous timer on new cell position (debounce behavior)
        // takeUntil(cancelPendingEnter$) cancels the timer on mouseleave/blur/drag
        this.subscription = areaCellPosition$.pipe(
            filter(() => !this.suppressHover), // Drag-suppression
            switchMap((position: AreaCellPosition) =>
                timer(120).pipe(
                    takeUntil(this.cancelPendingEnter$), // Cancel timer on mouseleave/blur/drag
                    map(() => position) // Emit position after 120ms if not cancelled
                )
            ),
            switchMap(async (position: AreaCellPosition) => {
                // T4.11: Reuse shared buildCellEventPayload with cache
                // targetItemId already resolved in Layer 1 (setupAreaCellPositionStream)
                const payload = await this.host.buildCellEventPayload(
                    position.clientX,
                    position.clientY,
                    position.targetItemId
                );
                return payload;
            }),
            filter((payload): payload is CellEventDetail => payload !== null),
            takeUntil(this.destroy$)
        ).subscribe(payload => {
            // Store payload for leave event
            this.enteredPayload = payload;
            // T4.17: Payload conforms to CellHoverEventDetail interface
            this.host.fireEvent(CELL_HOVER_CHANGED_EVENT, { ...payload, state: 'enter' });
        });

        // T4.13-T4.15: Leave events on: mouseleave, window blur, visibility change
        merge(
            fromEvent(this.host, 'mouseleave'),
            fromEvent(window, 'blur'),
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.hidden)
            )
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.cancelPendingEnter$.next(); // Cancel pending debounce timer
            this.dispatchLeave();
        });

        // T4.14-T4.16: Leave event bei jedem Zellwechsel
        areaCellPosition$.pipe(
            filter(() => !this.suppressHover),
            pairwise(), // [prev, curr] — pairwise() emits from 2nd value onwards (no leave for the very first cell, correct behavior)
            takeUntil(this.destroy$)
        ).subscribe(() => {
            // Dispatch leave for previous cell (reuses stored payload)
            // Safe: dispatchLeave() is no-op if enteredPayload is null (e.g. cell switch < 120ms before enter fired)
            this.dispatchLeave();
        });

        // T4.18-T4.19: Drag-suppression via RxJS - right-click (button 2) suppresses hover
        fromEvent<PointerEvent>(this.host, 'pointerdown').pipe(
            filter(e => e.button === 2),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.suppressHover = true;
            this.cancelPendingEnter$.next(); // Cancel pending debounce timer
            this.dispatchLeave();
        });

        fromEvent<PointerEvent>(this.host, 'pointerup').pipe(
            filter(e => e.button === 2),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.suppressHover = false;
        });
    }

    /**
     * Lifecycle: Called when host is disconnected from DOM
     */
    hostDisconnected(): void {
        // Signal all RxJS streams to complete
        this.destroy$.next();
        this.destroy$.complete();

        // Cleanup RxJS subscription (already handled by takeUntil, but explicit cleanup for safety)
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }

        // Reset stored payload
        this.enteredPayload = null;
    }

    /**
     * Dispatch leave event with stored payload from enter event.
     * Reuses the same payload to ensure consistency between enter/leave.
     */
    private dispatchLeave(): void {
        if (this.enteredPayload === null) {
            // No payload to reuse (should not happen in normal flow)
            return;
        }

        // Dispatch leave with stored payload + state: 'leave'
        this.host.fireEvent(CELL_HOVER_CHANGED_EVENT, {
            ...this.enteredPayload,
            state: 'leave' as const,
        });

        // Clear stored payload
        this.enteredPayload = null;
    }
}
